import React, { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../state/hooks";
import { closePdfDeliveryDialog } from "../../state/slices/pdfDeliveryDialogSlice";
import { resolvePdfDeliveryDialog } from "../../state/utils/pdfDeliveryDialogUtils";

export const PdfDeliveryDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isOpen, fileName, title } = useAppSelector(
    (state) => state.pdfDeliveryDialog
  );

  const printButtonRef = useRef<HTMLButtonElement>(null);
  const downloadButtonRef = useRef<HTMLButtonElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleCancel();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => printButtonRef.current?.focus(), 0);
    }
  }, [isOpen]);

  const handlePrint = () => {
    dispatch(closePdfDeliveryDialog());
    resolvePdfDeliveryDialog("print");
  };

  const handleDownload = () => {
    dispatch(closePdfDeliveryDialog());
    resolvePdfDeliveryDialog("download");
  };

  const handleCancel = () => {
    dispatch(closePdfDeliveryDialog());
    resolvePdfDeliveryDialog("cancel");
  };

  if (!isOpen) return null;

  const dialogTitle = title || "Document ready";

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[9998] bg-black/20 dark:bg-black/40"
        onClick={handleCancel}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] bg-white dark:bg-gray-800 shadow-2xl min-w-[320px] max-w-[480px] w-full"
        style={{ fontFamily: "system-ui, -apple-system, sans-serif", borderRadius: 4 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pdf-delivery-dialog-title"
      >
        {/* Title bar */}
        <div
          className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700"
          style={{ fontSize: 13, fontWeight: 600, userSelect: "none" }}
          id="pdf-delivery-dialog-title"
        >
          <span className="text-gray-900 dark:text-gray-100">{dialogTitle}</span>
        </div>

        {/* Body */}
        <div className="px-4 py-4">
          {/* Printer icon */}
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5 w-10 h-10 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-blue-600 dark:text-blue-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-0.5">
                What would you like to do?
              </p>
              {fileName && (
                <p
                  className="text-xs text-gray-500 dark:text-gray-400 truncate"
                  title={fileName}
                >
                  {fileName}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="px-4 py-3 flex flex-wrap items-center justify-end gap-2 border-t border-gray-200 dark:border-gray-700">
          <button
            ref={cancelButtonRef}
            onClick={handleCancel}
            className="px-4 py-1.5 text-sm font-medium border rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 dark:focus:ring-offset-gray-800 transition-colors duration-200"
            style={{ minWidth: 75 }}
          >
            Cancel
          </button>

          <button
            ref={downloadButtonRef}
            onClick={handleDownload}
            className="px-4 py-1.5 text-sm font-medium border rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 dark:focus:ring-offset-gray-800 transition-colors duration-200 flex items-center gap-1.5"
            style={{ minWidth: 120 }}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            Download PDF
          </button>

          <button
            ref={printButtonRef}
            onClick={handlePrint}
            className="px-4 py-1.5 text-sm font-medium rounded text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 dark:focus:ring-offset-gray-800 transition-all duration-200 flex items-center gap-1.5"
            style={{
              minWidth: 90,
              backgroundColor: "var(--color-primary-500, #35ba5b)",
            }}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
              />
            </svg>
            Print
          </button>
        </div>
      </div>
    </>
  );
};
